import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";
import type { BreederSex, QuantityPrecision } from "../domain/inventory";
import type { EggResult } from "../domain/breeding";

export interface BreedingOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  performedAt: string;
}

export interface BreedingOperationBuilderOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
  entityIdSource?: OperationIdSource;
}

const entityIdSource: OperationIdSource = {
  next: () => crypto.randomUUID(),
};

function create(
  context: BreedingOperationContext,
  operationType: string,
  entityId: string,
  payload: Readonly<Record<string, unknown>>,
  options?: BreedingOperationBuilderOptions,
  baseVersion?: number,
): OfflineOperation {
  if (
    !context.actorId ||
    !context.farmId ||
    !context.deviceId ||
    !context.performedAt ||
    !entityId
  ) {
    throw new Error("BREEDING_OPERATION_CONTEXT_REQUIRED");
  }

  return createOfflineOperation(
    {
      operationType,
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId,
      performedAt: context.performedAt,
      ...(baseVersion === undefined ? {} : { baseVersion: String(baseVersion) }),
      payload,
    },
    {
      ...(options?.clock ? { clock: options.clock } : {}),
      ...(options?.operationIdSource ? { idSource: options.operationIdSource } : {}),
    },
  );
}

function nextEntityId(options?: BreedingOperationBuilderOptions): string {
  return (options?.entityIdSource ?? entityIdSource).next();
}

export function buildBreederCreateOperation(
  context: BreedingOperationContext,
  input: {
    breederCode: string;
    sex: BreederSex;
    color?: string;
    visualDescription?: string;
    photoUrl?: string;
    originPairId?: string;
    targetTankId?: string;
  },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (!input.breederCode.trim()) throw new Error("BREEDER_CODE_REQUIRED");
  const breederId = nextEntityId(options);

  return create(
    context,
    "breeder.create",
    breederId,
    {
      breederId,
      breederCode: input.breederCode.trim(),
      sex: input.sex,
      ...(input.color?.trim() ? { color: input.color.trim() } : {}),
      ...(input.visualDescription?.trim()
        ? { visualDescription: input.visualDescription.trim() }
        : {}),
      ...(input.photoUrl?.trim() ? { photoUrl: input.photoUrl.trim() } : {}),
      ...(input.originPairId ? { originPairId: input.originPairId } : {}),
      ...(input.targetTankId ? { targetTankId: input.targetTankId } : {}),
    },
    options,
  );
}

export function buildBreederUpdateOperation(
  context: BreedingOperationContext,
  input: {
    breederId: string;
    breederVersion: number;
    breederCode: string;
    sex: BreederSex;
    status: "active" | "retired" | "deceased" | "exited";
    color?: string;
    visualDescription?: string;
    photoUrl?: string;
    targetTankId?: string;
  },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (
    !input.breederId ||
    !Number.isInteger(input.breederVersion) ||
    input.breederVersion < 0 ||
    !input.breederCode.trim()
  ) {
    throw new Error("BREEDER_UPDATE_INPUT_INVALID");
  }

  return create(
    context,
    "breeder.update",
    input.breederId,
    {
      breederId: input.breederId,
      expectedVersion: input.breederVersion,
      breederCode: input.breederCode.trim(),
      sex: input.sex,
      status: input.status,
      ...(input.color?.trim() ? { color: input.color.trim() } : {}),
      ...(input.visualDescription?.trim()
        ? { visualDescription: input.visualDescription.trim() }
        : {}),
      ...(input.photoUrl?.trim() ? { photoUrl: input.photoUrl.trim() } : {}),
      ...(input.targetTankId ? { targetTankId: input.targetTankId } : {}),
    },
    options,
    input.breederVersion,
  );
}

export function buildPairFormOperation(
  context: BreedingOperationContext,
  input: {
    pairCode: string;
    maleBreederId: string;
    femaleBreederId: string;
    targetTankId: string;
    notes?: string;
  },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (
    !input.pairCode.trim() ||
    !input.maleBreederId ||
    !input.femaleBreederId ||
    !input.targetTankId ||
    input.maleBreederId === input.femaleBreederId
  ) {
    throw new Error("PAIR_FORM_INPUT_INVALID");
  }

  const pairId = nextEntityId(options);

  return create(
    context,
    "pair.form",
    pairId,
    {
      pairId,
      pairCode: input.pairCode.trim(),
      maleBreederId: input.maleBreederId,
      femaleBreederId: input.femaleBreederId,
      targetTankId: input.targetTankId,
      ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
    },
    options,
  );
}

export function buildPairFormFromStockOperation(
  context: BreedingOperationContext,
  input: {
    destinationTankId: string;
    maleSourceStockGroupId: string;
    maleSourceStockVersion: number;
    maleVariety: string;
    maleVisualDescription?: string;
    femaleSourceStockGroupId: string;
    femaleSourceStockVersion: number;
    femaleVariety: string;
    femaleVisualDescription?: string;
  },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (
    !input.destinationTankId ||
    !input.maleSourceStockGroupId ||
    !input.femaleSourceStockGroupId ||
    !Number.isInteger(input.maleSourceStockVersion) ||
    input.maleSourceStockVersion < 0 ||
    !Number.isInteger(input.femaleSourceStockVersion) ||
    input.femaleSourceStockVersion < 0 ||
    !input.maleVariety.trim() ||
    !input.femaleVariety.trim()
  ) {
    throw new Error("PAIR_FORM_FROM_STOCK_INPUT_INVALID");
  }

  const pairId = nextEntityId(options);
  const maleBreederId = nextEntityId(options);
  const femaleBreederId = nextEntityId(options);

  return create(
    context,
    "pair.form_from_stock",
    pairId,
    {
      pairId,
      maleBreederId,
      femaleBreederId,
      destinationTankId: input.destinationTankId,
      maleSourceStockGroupId: input.maleSourceStockGroupId,
      maleExpectedStockVersion: input.maleSourceStockVersion,
      maleVariety: input.maleVariety.trim(),
      ...(input.maleVisualDescription?.trim()
        ? { maleVisualDescription: input.maleVisualDescription.trim() }
        : {}),
      femaleSourceStockGroupId: input.femaleSourceStockGroupId,
      femaleExpectedStockVersion: input.femaleSourceStockVersion,
      femaleVariety: input.femaleVariety.trim(),
      ...(input.femaleVisualDescription?.trim()
        ? { femaleVisualDescription: input.femaleVisualDescription.trim() }
        : {}),
    },
    options,
  );
}

export function buildPairMoveOperation(
  context: BreedingOperationContext,
  input: { pairId: string; pairVersion: number; targetTankId: string },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (
    !input.pairId ||
    !input.targetTankId ||
    !Number.isInteger(input.pairVersion) ||
    input.pairVersion < 0
  ) {
    throw new Error("PAIR_MOVE_INPUT_INVALID");
  }

  return create(
    context,
    "pair.move",
    input.pairId,
    {
      pairId: input.pairId,
      expectedVersion: input.pairVersion,
      targetTankId: input.targetTankId,
    },
    options,
    input.pairVersion,
  );
}

export function buildPairEndOperation(
  context: BreedingOperationContext,
  input: {
    pairId: string;
    pairVersion: number;
    mode: "separate" | "exit";
    reason?: string;
    maleTargetTankId?: string;
    femaleTargetTankId?: string;
  },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (!input.pairId || !Number.isInteger(input.pairVersion) || input.pairVersion < 0) {
    throw new Error("PAIR_END_INPUT_INVALID");
  }

  if (
    input.mode === "separate" &&
    input.maleTargetTankId &&
    input.femaleTargetTankId &&
    input.maleTargetTankId === input.femaleTargetTankId
  ) {
    throw new Error("PAIR_SEPARATION_TANKS_MUST_DIFFER");
  }

  return create(
    context,
    "pair.end",
    input.pairId,
    {
      pairId: input.pairId,
      expectedVersion: input.pairVersion,
      mode: input.mode,
      ...(input.reason?.trim() ? { reason: input.reason.trim() } : {}),
      ...(input.maleTargetTankId ? { maleTargetTankId: input.maleTargetTankId } : {}),
      ...(input.femaleTargetTankId ? { femaleTargetTankId: input.femaleTargetTankId } : {}),
    },
    options,
    input.pairVersion,
  );
}

export function buildBreedingSpawnOperation(
  context: BreedingOperationContext,
  input: { pairId: string; pairVersion: number; tankId: string },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (
    !input.pairId ||
    !input.tankId ||
    !Number.isInteger(input.pairVersion) ||
    input.pairVersion < 0
  ) {
    throw new Error("BREEDING_SPAWN_INPUT_INVALID");
  }

  const cycleId = nextEntityId(options);
  return create(
    context,
    "breeding.spawn",
    cycleId,
    {
      cycleId,
      pairId: input.pairId,
      expectedPairVersion: input.pairVersion,
      expectedTankId: input.tankId,
    },
    options,
    input.pairVersion,
  );
}

export function buildBreedingEggResultOperation(
  context: BreedingOperationContext,
  input: {
    cycleId: string;
    cycleVersion: number;
    eggResult: EggResult;
    hatchPercent?: number;
    fryQuantity?: number;
    quantityPrecision?: QuantityPrecision;
  },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (!input.cycleId || !Number.isInteger(input.cycleVersion) || input.cycleVersion < 1) {
    throw new Error("BREEDING_EGG_RESULT_INPUT_INVALID");
  }

  if (
    input.hatchPercent !== undefined &&
    (!Number.isInteger(input.hatchPercent) || input.hatchPercent < 0 || input.hatchPercent > 100)
  ) {
    throw new Error("BREEDING_HATCH_PERCENT_INVALID");
  }

  const success = input.eggResult === "hatched" || input.eggResult === "partial_hatch";
  const precision = input.quantityPrecision ?? "unknown";
  if (precision === "unknown" && input.fryQuantity !== undefined) {
    throw new Error("UNKNOWN_STOCK_QUANTITY_MUST_BE_EMPTY");
  }
  if (
    precision !== "unknown" &&
    (!Number.isInteger(input.fryQuantity) || (input.fryQuantity ?? -1) < 0)
  ) {
    throw new Error("KNOWN_STOCK_QUANTITY_REQUIRED");
  }

  const batchId = success ? nextEntityId(options) : undefined;
  const stockGroupId = success ? nextEntityId(options) : undefined;

  return create(
    context,
    "breeding.egg_result",
    input.cycleId,
    {
      cycleId: input.cycleId,
      expectedCycleVersion: input.cycleVersion,
      eggResult: input.eggResult,
      ...(input.hatchPercent === undefined ? {} : { hatchPercent: input.hatchPercent }),
      ...(success
        ? {
            batchId,
            stockGroupId,
            quantityPrecision: precision,
            ...(input.fryQuantity === undefined ? {} : { fryQuantity: input.fryQuantity }),
          }
        : {}),
    },
    options,
    input.cycleVersion,
  );
}

export function buildBreedingFreeSwimOperation(
  context: BreedingOperationContext,
  input: { cycleId: string; cycleVersion: number },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (!input.cycleId || !Number.isInteger(input.cycleVersion) || input.cycleVersion < 1) {
    throw new Error("BREEDING_FREE_SWIM_INPUT_INVALID");
  }

  return create(
    context,
    "breeding.free_swim",
    input.cycleId,
    { cycleId: input.cycleId, expectedCycleVersion: input.cycleVersion },
    options,
    input.cycleVersion,
  );
}

export function buildBreedingStageOperation(
  context: BreedingOperationContext,
  input: {
    cycleId: string;
    cycleVersion: number;
    stage: "parent_feeding" | "separated" | "growout" | "failed" | "completed";
    failureReason?: string;
  },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (!input.cycleId || !Number.isInteger(input.cycleVersion) || input.cycleVersion < 1) {
    throw new Error("BREEDING_STAGE_INPUT_INVALID");
  }

  return create(
    context,
    "breeding.stage",
    input.cycleId,
    {
      cycleId: input.cycleId,
      expectedCycleVersion: input.cycleVersion,
      stage: input.stage,
      ...(input.failureReason?.trim() ? { failureReason: input.failureReason.trim() } : {}),
    },
    options,
    input.cycleVersion,
  );
}

export function buildStockTransferOperation(
  context: BreedingOperationContext,
  input: {
    stockGroupId: string;
    stockVersion: number;
    targetTankId: string;
    moveQuantity?: number;
  },
  options?: BreedingOperationBuilderOptions,
): OfflineOperation {
  if (
    !input.stockGroupId ||
    !input.targetTankId ||
    !Number.isInteger(input.stockVersion) ||
    input.stockVersion < 0
  ) {
    throw new Error("STOCK_TRANSFER_INPUT_INVALID");
  }

  if (
    input.moveQuantity !== undefined &&
    (!Number.isInteger(input.moveQuantity) || input.moveQuantity <= 0)
  ) {
    throw new Error("STOCK_TRANSFER_QUANTITY_INVALID");
  }

  const childStockGroupId = input.moveQuantity === undefined ? undefined : nextEntityId(options);

  return create(
    context,
    "stock_group.transfer",
    input.stockGroupId,
    {
      stockGroupId: input.stockGroupId,
      expectedVersion: input.stockVersion,
      targetTankId: input.targetTankId,
      ...(input.moveQuantity === undefined ? {} : { moveQuantity: input.moveQuantity }),
      ...(childStockGroupId ? { childStockGroupId } : {}),
    },
    options,
    input.stockVersion,
  );
}
